import React, {
  ReactElement,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Button,
  CircularProgress,
  TextField,
  Checkbox,
  Typography,
  InputAdornment,
  IconButton,
  Dialog,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Box,
  Select,
  MenuItem,
  InputLabel,
  FormHelperText,
  FormControl,
  styled,
  ButtonProps,
  FormGroup,
  useTheme,
  Switch,
} from "@mui/material";
import Tooltip, { TooltipProps, tooltipClasses } from "@mui/material/Tooltip";
import MuiCheckbox from "@mui/material/Checkbox";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useField, useFormikContext } from "formik";
import Utils from "shared/helpers/Utils";
import { useProgress } from "context/ProgressProvider";
import { DateCalendar } from "@mui/x-date-pickers";
import dayjs from "dayjs";

const AppThemeButton = styled(Button)<ButtonProps>(() => ({
  "&:hover": {},
  "&.Mui-disabled": {
    opacity: 0.7,
  },
}));

export interface ButtonInterface {
  className?: string;
  title: any;
  disabled?: boolean;
  fullWidth?: boolean;
  isLoading?: boolean;
  loadingText?: string;
  endIcon?: any;
  startIcon?: any;
  onClick?: any;
  variant?: "text" | "outlined" | "contained";
  color?: "primary" | "secondary";
}

interface CheckboxOption {
  value: string;
  label: string;
}

interface AppFormikCheckboxGroupProps {
  name: string;
  label?: string;
  outerLabel?: boolean;
  options: CheckboxOption[];
  readOnly?: boolean;
}

export const AppButton = ({
  className,
  title,
  disabled,
  fullWidth,
  isLoading,
  loadingText,
  endIcon,
  startIcon,
  onClick,
  variant,
  color,
}: ButtonInterface): ReactElement => {
  const classNames = ["trans-3d-hover"];
  if (className) {
    classNames.push(className);
  }
  return (
    <AppThemeButton
      type="submit"
      className={classNames.join(" ")}
      disabled={disabled}
      fullWidth={fullWidth !== undefined ? fullWidth : false}
      startIcon={startIcon}
      endIcon={endIcon}
      onClick={onClick}
      variant={variant || "contained"}
      color={color || "primary"}
    >
      {isLoading && (
        <>
          <CircularProgress />
          {(loadingText === "" || loadingText) && <span>{loadingText}</span>}
          {loadingText !== "" && !loadingText && <span>PROCESSING</span>}
        </>
      )}
      {!isLoading && <>{title}</>}
    </AppThemeButton>
  );
};

export const AppPrimaryButton = (props: ButtonInterface): ReactElement => {
  const { className } = props;
  return <AppButton {...props} className={`btn-primary ${className}`} />;
};

export const AppSecondaryButton = (props: ButtonInterface): ReactElement => {
  const { className } = props;
  return <AppButton {...props} className={`btn-secondary ${className}`} />;
};

export const AppPrimaryButtonOutlined = (
  props: ButtonInterface,
): ReactElement => {
  const { className } = props;
  return (
    <AppButton {...props} className={`btn-primary-outlined ${className}`} />
  );
};

export const AppSecondaryButtonOutlined = (
  props: ButtonInterface,
): ReactElement => {
  const { className } = props;
  return (
    <AppButton {...props} className={`btn-secondary-outlined ${className}`} />
  );
};

export const AppTeritaryButton = (props: ButtonInterface): ReactElement => {
  const { className } = props;
  return <AppButton {...props} className={`btn-tertiary ${className}`} />;
};

export const AppDarkButton = (props: ButtonInterface): ReactElement => {
  const { className } = props;
  return <AppButton {...props} className={`btn-dark ${className}`} />;
};

export const AppPlainButton = (props: ButtonInterface): ReactElement => {
  const { className } = props;
  return <AppButton {...props} className={`btn-plain ${className}`} />;
};

export const AppFormikTextField = (props: any): ReactElement => {
  const {
    className,
    label,
    outerLabel,
    size,
    type,
    inputProps,
    onChange,
    placeholder,
    slotProps,
    removeSpacing,
    returnAsString,
    sx,
    helperText = true,
    allowComma,
    customIsoDollar,
    customIsoDollarDecimal,
    blockedAndReadOnly = false,
  } = props;

  const handleChange = (ev) => {
    const input = ev.target;
    let rawValue = input.value;

    let processedValue = allowComma ? rawValue : rawValue.replace(/,/g, "");

    if (removeSpacing) {
      processedValue = processedValue
        .toLowerCase()
        .replace(/\b\w/g, (char) => char.toUpperCase())
        .replace(/\s+/g, "");
    }

    if (type === "customAUPhone") {
      if (!rawValue.startsWith("+61 ")) {
        rawValue = rawValue.replace(/^\+?61\s?/, "");
        rawValue = "+61 " + rawValue;
        processedValue = rawValue;
      }
    }

    if (customIsoDollar) {
      processedValue = Utils.formatCurrency(rawValue);
    }

    if (customIsoDollarDecimal) {
      processedValue = Utils.formatCurrencyWithDecimal(rawValue);
    }

    const finalValue =
      type === "number"
        ? returnAsString
          ? String(rawValue)
          : Number(rawValue)
        : processedValue;

    ev.target.value = finalValue as any;

    field.onChange(ev);
    if (onChange) {
      onChange(ev);
    }
  };

  const [field, meta] = useField(props);
  return (
    <>
      {outerLabel && (
        <Typography variant="body1" className="formik-text-field-container">
          {label}
        </Typography>
      )}

      <TextField
        className={outerLabel ? `outer-label ${className}` : `${className}`}
        error={Boolean(meta.touched && meta.error)}
        fullWidth
        helperText={helperText && meta.touched && meta.error}
        margin="normal"
        name={field.name}
        label={outerLabel ? "" : label}
        placeholder={placeholder}
        type={type}
        value={meta.value}
        variant="outlined"
        onBlur={field.onBlur}
        onChange={handleChange}
        size={size || "medium"}
        InputProps={inputProps}
        slotProps={slotProps}
        sx={sx}
        disabled={blockedAndReadOnly}
      />
    </>
  );
};

export const AppFormikPasswordField = (props: any): ReactElement => {
  const { label, size, outerLabel, placeholder, className } = props;
  const [field, meta] = useField(props);
  const [showPassword, setShowPassword] = useState(false);
  return (
    <>
      {outerLabel && (
        <Typography variant="body1" className="formik-text-field-container">
          {label}
        </Typography>
      )}
      <TextField
        className={outerLabel ? `outer-label ${className}` : `${className}`}
        error={Boolean(meta.touched && meta.error)}
        fullWidth
        helperText={meta.touched && meta.error}
        margin="normal"
        name={field.name}
        label={outerLabel ? "" : label}
        placeholder={placeholder}
        type={showPassword ? "text" : "password"}
        value={meta.value}
        variant="outlined"
        onBlur={field.onBlur}
        onChange={field.onChange}
        size={size || "medium"}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => {
                  setShowPassword(!showPassword);
                }}
                edge="end"
                className="text-primary"
              >
                {showPassword ? <Visibility /> : <VisibilityOff />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
    </>
  );
};

export const AppFormikCheckboxGroup = (
  props: AppFormikCheckboxGroupProps,
): ReactElement => {
  const { label, outerLabel, readOnly, options } = props;
  const [field, meta, helpers] = useField(props.name);
  const { currentProfile } = useProgress();
  const theme = useTheme();
  const currentValues = Array.isArray(field.value)
    ? field.value
    : field.value
      ? [field.value]
      : [];

  const handleChange = (value: string, checked: boolean) => {
    if (readOnly) return;

    let newValues: string[];

    if (checked) {
      if (currentValues.includes(value)) {
        return;
      }
      newValues = [...currentValues, value];
    } else {
      newValues = currentValues.filter((v) => v !== value);
    }

    if (newValues.length === 0) {
      helpers.setValue("");
    } else if (newValues.length === 1) {
      helpers.setValue(newValues[0]);
    } else {
      helpers.setValue(newValues);
    }
  };

  const isOptionDisabled = (optionValue: string): boolean => {
    if (readOnly) return true;
    return false;
  };

  return (
    <>
      {outerLabel && label && (
        <Typography
          variant="body1"
          className="text-secondary sofia-pro-regular"
        >
          {label}
        </Typography>
      )}
      <FormControl
        error={Boolean(meta.touched && meta.error)}
        margin="normal"
        fullWidth
      >
        <FormGroup
          row
          sx={{
            justifyContent: "space-between",
            [theme.breakpoints.down("md")]: {
              display: "grid",
              justifyContent: "unset",
              gap: "0.5rem",
            },
          }}
        >
          {options.map((option, index) => {
            const isChecked = currentValues.includes(option.value);
            const isDisabled = isOptionDisabled(option.value);

            return (
              <FormControlLabel
                key={option.value}
                sx={{
                  flex: options.length <= 3 ? "1" : "unset",
                  maxWidth:
                    options.length <= 3 ? `${100 / options.length}%` : "auto",
                  border: `1px solid ${isChecked ? "#0053FD" : "#E5E5E5"}`,
                  borderRadius: "8px",
                  margin:
                    index === 0
                      ? "0 6px 0 0"
                      : index === options.length - 1
                        ? "0 0 0 6px"
                        : "0 6px",
                  opacity: isDisabled ? 0.5 : 1,
                  [theme.breakpoints.down("md")]: {
                    maxWidth: "100%",
                    margin: 0,
                  },
                }}
                control={
                  <Checkbox
                    checked={isChecked}
                    onChange={(e) =>
                      handleChange(option.value, e.target.checked)
                    }
                    disabled={
                      isDisabled ||
                      (currentProfile.isAllowedToEdit &&
                        !currentProfile.isAllowedToEdit?.directors) ||
                      false
                    }
                  />
                }
                label={
                  <Typography sx={{ fontWeight: "bold" }}>
                    {option.label}
                  </Typography>
                }
              />
            );
          })}
        </FormGroup>
        {meta.touched && meta.error && (
          <FormHelperText>{meta.error}</FormHelperText>
        )}
      </FormControl>
    </>
  );
};

export const AppFormikTextArea = (props: any): ReactElement => {
  const { label, outerLabel, readOnly, rows, placeholder } = props;
  const [field, meta] = useField(props);
  return (
    <>
      {outerLabel && (
        <Typography
          variant="body1"
          className="text-secondary sofia-pro-regular"
        >
          {label}
        </Typography>
      )}
      <TextField
        multiline
        rows={rows}
        error={Boolean(meta.touched && meta.error)}
        fullWidth
        helperText={meta.touched && meta.error}
        margin="normal"
        name={field.name}
        label={outerLabel ? "" : label}
        placeholder={placeholder ? placeholder : ""}
        value={meta.value}
        variant="outlined"
        onBlur={field.onBlur}
        onChange={field.onChange}
        InputProps={{
          readOnly,
        }}
      />
    </>
  );
};

export const AppCheckBox = (props: any): ReactElement => {
  const { label, checked, onChange } = props;
  return (
    <FormControlLabel
      label={label}
      control={
        <Checkbox checked={checked} onChange={onChange} color="primary" />
      }
    />
  );
};

export const ConfirmDialog = (props: any): ReactElement => {
  const {
    title,
    subTitle,
    showConfirm,
    cancelTitle,
    successTitle,
    onSuccess,
    onCancel,
    onCloseDialog,
    hideNegative,
  } = props;

  // Actions
  const actionPositive = (): void => {
    onSuccess();
  };
  const actionNegative = (): void => {
    onCancel();
  };
  const onClose = (): void => {
    if (onCloseDialog) {
      onCloseDialog();
    }
  };

  // UI Elements
  return (
    <>
      <Dialog fullWidth maxWidth="sm" open={showConfirm} onClose={onClose}>
        <Box padding={2}>
          <DialogContent className="dialog-content-container ">
            <Typography className="white" variant="h3">
              {title}
            </Typography>
            <Typography className="white">{subTitle}</Typography>
          </DialogContent>
          <DialogActions>
            {!hideNegative && (
              <AppSecondaryButton
                className="btn-small"
                title={cancelTitle}
                onClick={actionNegative}
              />
            )}
            <AppPrimaryButton
              className="btn-small"
              title={successTitle}
              onClick={actionPositive}
            />
          </DialogActions>
        </Box>
      </Dialog>
    </>
  );
};

export const AppFormikSelect = (props: any): ReactElement => {
  const {
    name,
    items,
    titleKey,
    valueKey,
    readOnly,
    onChange,
    outerLabel,
    label,
    placeholder,
    onOpen,
    showLoading,
    iconComponent,
    className,
    labelSize,
    helperText,
    disabled,
  } = props;
  const [field, meta] = useField(props);
  return (
    <>
      {outerLabel && (
        <Typography
          variant="body1"
          fontSize={labelSize || ""}
          className="formik-select-field-container"
        >
          {label}
        </Typography>
      )}
      <Select
        onOpen={onOpen}
        className={outerLabel ? `outer-label ${className}` : className}
        error={Boolean(meta.touched && meta.error)}
        label={outerLabel ? undefined : label || placeholder}
        fullWidth
        margin="none"
        variant="outlined"
        value={meta.value}
        name={field.name}
        onBlur={(e: any) => {
          field.onBlur(e);
          if (onChange) onChange(e);
        }}
        onChange={(e: any) => {
          field.onChange(e);
          if (onChange) onChange(e);
        }}
        inputProps={{ readOnly }}
        displayEmpty
        IconComponent={iconComponent}
      >
        {placeholder && (
          <MenuItem disabled key={`${name}_${placeholder}`} value="">
            <Typography className="text-tertiary" sx={{ fontSize: "16px" }}>
              {placeholder}
            </Typography>
          </MenuItem>
        )}
        {items.length === 0 && showLoading && (
          <MenuItem disabled key={`${name}_loading`} value="">
            <Typography className="text-tertiary">Loading...</Typography>
          </MenuItem>
        )}
        {items.map((item: any, index: number) => (
          <MenuItem
            key={`${index}_${field.name}_${item}`}
            value={item[valueKey]}
            className="black"
          >
            {item[titleKey]}
          </MenuItem>
        ))}
      </Select>
      {helperText && meta.touched && meta.error && (
        <FormHelperText error>{meta.error}</FormHelperText>
      )}
    </>
  );
};
export const AppSelectV2 = (props: any): ReactElement => {
  const {
    value,
    name,
    items,
    titleKey,
    valueKey,
    onBlur,
    onChange,
    readOnly,
    outerLabel,
    placeholder,
    label,
  } = props;
  return (
    <>
      <FormControl
        variant="outlined"
        className="select-box text-primary mt-2"
        fullWidth
      >
        {outerLabel && <InputLabel>{label || placeholder}</InputLabel>}
        <Select
          value={value}
          onBlur={onBlur}
          name={name}
          onChange={onChange}
          className="black "
          readOnly={readOnly}
          label={outerLabel ? placeholder : ""}
        >
          {placeholder && (
            <MenuItem disabled key={`${name}_${placeholder}`} value="">
              <Typography className="text-tertiary">{placeholder}</Typography>
            </MenuItem>
          )}
          {items.map((item: any): any => (
            <MenuItem key={`${name}`} value={item[valueKey]} className="black">
              {item[titleKey]}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </>
  );
};

export const AppMultiSelect = (props: any): ReactElement => {
  const {
    value,
    name,
    items,
    titleKey,
    valueKey,
    onBlur,
    onChange,
    readOnly,
    outerLabel,
    label,
  } = props;
  return (
    <>
      {outerLabel && (
        <Typography variant="body1" className="text-primary">
          {label}
        </Typography>
      )}
      <FormControl
        variant="outlined"
        className="select-box white mt-2"
        fullWidth
      >
        <Select
          multiple
          value={value}
          onBlur={onBlur}
          name={name}
          onChange={onChange}
          className="black "
          readOnly={readOnly}
        >
          {items.map((item: any): any => (
            <MenuItem key={`${name}`} value={item[valueKey]} className="black">
              {item[titleKey]}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </>
  );
};

export const AppSelect = (props: any): ReactElement => {
  const { placeholder, onChange, label, outerLabel, items, readOnly } = props;
  const [field, meta] = useField(props);
  return (
    <>
      {outerLabel && (
        <Typography variant="body1" className="formik-select-field-container">
          {label}
        </Typography>
      )}

      <Select
        className={outerLabel ? "outer-label" : ""}
        error={Boolean(meta.touched && meta.error)}
        label={outerLabel ? undefined : label}
        fullWidth
        value={meta.value}
        name={field.name}
        variant="outlined"
        onBlur={(e: any) => {
          field.onBlur(e);
          if (onChange) onChange(e);
        }}
        onChange={(e: any) => {
          field.onChange(e);
          if (onChange) onChange(e);
        }}
        inputProps={{ readOnly }}
      >
        {placeholder && (
          <MenuItem disabled key={`${field.name}_${placeholder}`} value="">
            <Typography className="text-tertiary">{placeholder}</Typography>
          </MenuItem>
        )}
        {items.map((item: any) => (
          <MenuItem
            key={`${field.name}_${item}`}
            value={item}
            className="black"
          >
            {item}
          </MenuItem>
        ))}
      </Select>

      {meta.touched && meta.error && (
        <FormHelperText error>{meta.error}</FormHelperText>
      )}
    </>
  );
};

export const AppRadioBox = (props: any): ReactElement => {
  const { onChange, label, icon, checked, checkedIcon, disabled } = props;
  return (
    <FormControlLabel
      control={
        <MuiCheckbox
          checked={checked}
          icon={icon}
          disabled={disabled}
          checkedIcon={checkedIcon}
          disableRipple
          color="primary"
        />
      }
      label={label}
      onChange={onChange}
    />
  );
};

export const BlackTooltip = styled(({ className, ...props }: any) => (
  <Tooltip
    arrow
    classes={{ popper: className }}
    PopperProps={{ style: { zIndex: 2 } }}
    enterTouchDelay={10}
    disableHoverListener={props?.isMobile || false}
    {...props}
  />
))(({ theme }) => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.common.black,
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.black,
  },
}));

export const AppFormikTextFieldNumber = (props: any): ReactElement => {
  const {
    className,
    label,
    outerLabel,
    size,
    type,
    inputProps,
    onChange,
    onEnter,
    placeholder,
    sx,
    positiveLookAhead,
    autoFocus,
    autoComplete,
    onElementSizing,
    onBlur,
    onFocus,
  } = props;
  const [field, meta] = useField(props);
  // const [value, setValue] = useState<string>("")
  const formik = useFormikContext();
  const fieldHelper = formik.getFieldHelpers(field.name);
  const innerFormHelper = useRef<null | HTMLDivElement>(null);
  const reinputProps = useMemo(
    () =>
      typeof inputProps === "object" &&
      typeof inputProps.inputProps !== "undefined"
        ? {
            ...inputProps,
            inputProps: {
              ...inputProps.inputProps,
              "aria-labelledby": outerLabel ? "textfield-typography" : "",
            },
          }
        : {
            ...inputProps,
            inputProps: {
              "aria-labelledby": outerLabel ? "textfield-typography" : "",
            },
          },
    [inputProps, outerLabel],
  );

  useLayoutEffect(() => {
    const layout = innerFormHelper.current?.getBoundingClientRect();

    if (typeof onElementSizing === "function") {
      onElementSizing({ height: layout?.height, width: layout?.width });
    }
  }, [meta.error, meta.touched]);

  return (
    <>
      {outerLabel && (
        <Typography
          variant="body1"
          className="formik-text-field-container"
          id="number-textfield-typography"
        >
          {label}
        </Typography>
      )}

      <TextField
        className={outerLabel ? `outer-label ${className}` : className}
        sx={{
          ...sx,
          "& input::placeholder": {
            fontWeight: "400",
            fontSize: "14px",
            color: Boolean(meta.touched && meta.error)
              ? "#d33232 !important"
              : "#aba8a6 !important",
            opacity: "1 !important",
          },
        }}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        error={Boolean(meta.touched && meta.error)}
        fullWidth
        helperText={meta.touched && meta.error}
        margin="normal"
        name={field.name}
        label={outerLabel ? "" : label}
        placeholder={placeholder}
        type={type}
        value={Number.isNaN(meta.value) ? "" : meta.value}
        variant="outlined"
        onBlur={(event: any) => {
          field.onBlur(event);
          if (typeof onBlur === "function") {
            onBlur(event);
          }
        }}
        onFocus={(e) => {
          if (typeof onFocus === "function") {
            onFocus(e);
          }
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            if (meta.touched === false) {
              fieldHelper.setTouched(true);
            }
            if (typeof onEnter === "function") {
              onEnter(event);
            }
          }
        }}
        onChange={(event: any) => {
          field.onChange(event);

          if (/^\d+$/.test(event.target.value ?? "") === false) {
            fieldHelper.setError("Only Numbers Allowed");
            fieldHelper.setTouched(true, false);
          }
          const value = event.target.value.replace(/\D/, "");
          fieldHelper.setValue(
            typeof positiveLookAhead === "boolean" && positiveLookAhead
              ? value.replace(/^0/, "")
              : value,
            false,
          );

          if (onChange) {
            onChange(event);
          }
        }}
        size={size || "medium"}
        ref={innerFormHelper}
        InputProps={{
          ...reinputProps,

          classes: {
            root: "input-container",
          },
        }}
      />
    </>
  );
};

export const IOSSwitch = styled(Switch)(({ theme }) => ({
  width: 70,
  height: 34,
  padding: 0,
  "& .MuiSwitch-switchBase": {
    padding: 0,
    margin: 2,
    transitionDuration: "300ms",
    "&.Mui-checked": {
      transform: "translateX(36px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        backgroundColor: "#2D68F7",
        opacity: 1,
        border: 0,
        "&:before": {
          content: '"YES"',
          position: "absolute",
          left: 8,
          top: "50%",
          transform: "translateY(-50%)",
          fontSize: 11,
          fontWeight: 600,
          color: "#fff",
        },
      },
    },
  },
  "& .MuiSwitch-thumb": {
    boxSizing: "border-box",
    width: 30,
    height: 30,
    boxShadow: "0 2px 4px 0 rgb(0 0 0 / 20%)",
  },
  "& .MuiSwitch-track": {
    borderRadius: 34 / 2,
    backgroundColor: "#e9e9ea",
    opacity: 1,
    transition: theme.transitions.create(["background-color"], {
      duration: 500,
    }),
    "&:after": {
      content: '"NO"',
      position: "absolute",
      right: 8,
      top: "50%",
      transform: "translateY(-50%)",
      fontSize: 11,
      fontWeight: 600,
      color: "#999",
    },
  },
}));

export const CustomInput: any = styled(TextField)((): any => ({}));

export const CustomSelectInput: any = styled(Select)((): any => ({}));

export const StyledDialog = styled(Dialog)(({ theme }) => ({
  "&.MuiDialog-root": {
    overflow: "auto !important",
  },
  "& .MuiDialog-container": {
    overflow: "auto !important",
  },
  "& .MuiPaper-root": {
    maxHeight: "100% !important",
    overflow: "hidden !important",
  },
  [theme.breakpoints.down("md")]: {
    "& .MuiDialog-container": {
      height: "auto !important",
    },
  },
}));
